import React, { useState } from "react";
import { Card, CardBody, CardTitle, Button } from "reactstrap";
import Dropzone from "react-dropzone";
import { toast } from "react-toastify";
import { useBase64Converter } from "hooks/useBase64Converter";

interface FileWithPreview {
  id: number;
  url: string;
  name?: string;
}

export const BulkUploadInput = ({
  setFieldValue,
  initialFiles = [],
}: {
  setFieldValue: any;
  initialFiles?: FileWithPreview[];
}) => {
  const [selectedFiles, setSelectedFiles] =
    useState<FileWithPreview[]>(initialFiles);
  const [excelFiles, setExcelFiles] = useState<File[]>([]);
  const { convertFilesToBase64 } = useBase64Converter();
  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  // const extractColumnNames = (file: File) => {
  //   return new Promise<string[]>((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.onload = (e) => {
  //       const content = e.target?.result as string;
  //       const fileType = file.type;

  //       if (fileType === "text/csv") {
  //         // Handle CSV file
  //         const lines = content.split("\n");
  //         const columnNames = lines[0].split(",").map((name) => name.trim());
  //         resolve(columnNames);
  //       } else if (fileType === "text/plain") {
  //         // Handle plain text files (if needed)
  //         const lines = content.split("\n");
  //         const columnNames = lines[0].split(",").map((name) => name.trim());
  //         resolve(columnNames);
  //       } else {
  //         console.log("Unsupported file type");
  //       }
  //     };
  //     reader.onerror = (error) => reject(error);
  //     reader.readAsText(file); // Read as text for CSV and plain text
  //   });
  // };

  const handleAcceptedFiles = async (acceptedFiles: File[]) => {
    if (selectedFiles.length >= 3) {
      toast.error("Maximum number of files reached");
      return;
    }
    const uniqueFiles = acceptedFiles.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error("File size exceeds the maximum limit of 3 MB");
        return false; // Exclude the file if it exceeds the size limit
      }
      if (
        selectedFiles.some((selectedFile) => selectedFile.name === file.name)
      ) {
        toast.error("This file has already been added");
      }
      return !selectedFiles.some(
        (selectedFile) => selectedFile.name === file.name
      );
    });

    const filesWithPreview = uniqueFiles.map((file, index) => ({
      id: selectedFiles.length + index + 1,
      url: URL.createObjectURL(file),
      name: file.name,
    }));

    const updatedFiles = [...selectedFiles, ...filesWithPreview];

    const newFiles = uniqueFiles.map((file) => file);

    const files = [...newFiles, ...excelFiles];

    setExcelFiles(files.slice(0, 3));

    setSelectedFiles(updatedFiles.slice(0, 3));

    const base64Files = convertFilesToBase64(files);
    // Update Formik files with the new format
    setFieldValue("spreadsheet", base64Files);

    // for (const file of uniqueFiles) {
    //   if (file.type === "application/xlsx" || file.type === "text/csv") {
    //     const columnNames = await extractColumnNames(file);
    //     console.log("Column Names:", columnNames);
    //   }
    // }
  };

  const handleDelete = (index: number) => {
    const newfiles = selectedFiles.filter(
      (_, i) => i !== index
    ) as FileWithPreview[];

    const filesNotInNewfiles = initialFiles?.filter(
      (initialImage) =>
        !newfiles.some((newImage) => newImage.url === initialImage.url)
    );

    const filesToDeleteIds = filesNotInNewfiles.map((image) => image.id);
    setFieldValue("files_to_delete", filesToDeleteIds);

    const newFiles = excelFiles.filter((_, i) => i !== index);
    setSelectedFiles(newfiles);
    setExcelFiles(newFiles);

    const base64Files = convertFilesToBase64(excelFiles);

    setFieldValue("files", base64Files);
  };

  return (
    <Card className="mb-0">
      <CardBody>
        <CardTitle className="mb-3">Excel or CSV files</CardTitle>

        <>
          {selectedFiles.length < 3 && (
            <Dropzone
              onDrop={handleAcceptedFiles}
              accept={{
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                  [],
                "text/csv": [],
              }}
              multiple={false}
              maxFiles={3}
            >
              {({ getRootProps, getInputProps }) => (
                <div className="dropzone" {...getRootProps()}>
                  <input {...getInputProps()} />
                  <div className="dz-message needsclick">
                    <div className="mb-3">
                      <i className="display-4 text-muted bx bxs-cloud-upload" />
                    </div>
                    <h4>Drop files here or click to upload. (Max 3 files)</h4>
                  </div>
                </div>
              )}
            </Dropzone>
          )}
          <ul className="list-unstyled mb-0" id="file-previews">
            {selectedFiles.map((file, index) => (
              <li className="mt-2 dz-image-preview" key={index}>
                <div className="border rounded">
                  <div className="d-flex flex-wrap gap-2 p-2">
                    <div className="flex-shrink-0 me-3">
                      {index < 3 && (
                        <i className="bx bxs-file font-size-24"></i>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <div className="pt-1">
                        <h5 className="fs-md mb-1" data-dz-name>
                          {file.name} {/* Displaying the file name */}
                        </h5>
                        <strong
                          className="error text-danger"
                          data-dz-errormessage
                        ></strong>
                      </div>
                    </div>
                    <div className="flex-shrink-0 ms-3">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(index)} // Use the handleDelete function
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      </CardBody>
    </Card>
  );
};
